import {
  IconButton, Box, Dialog, DialogTitle, DialogContent,
  DialogContentText, DialogActions, Button, Switch, TextField,
  Typography, FormControl, InputLabel, Select, MenuItem, Tooltip,
  InputAdornment, CircularProgress, Paper,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer,
  TablePagination,
} from "@mui/material";
import EditIcon from '@mui/icons-material/Edit';
import FilterListIcon from '@mui/icons-material/FilterList';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import { useState, useEffect } from "react";
import AppService from "../../../../Configurations/Appservice";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { updateScreenTitle, showSuccessMessage } from "../../../../LayoutContainers/AppLayout/redux/AppSlice";

// Theme color
const THEME_COLOR = '#009688';

const CustomerServiceMappings = () => {
  // Server-paginated row state. `rows` is just the current page slice;
  // `totalCount` comes from the API's `totalElements` for TablePagination.
  const [rows, setRows] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);

  // `searchInput` is the live value bound to the TextField; `searchTerm` is the
  // debounced value actually sent to the API to avoid one request per keystroke.
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedOrg, setSelectedOrg] = useState('all'); // 'all' or org UUID
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  // Organisations dropdown (full list of customer orgs, fetched once).
  const [organisations, setOrganisations] = useState([]);

  // Dialog state for status change
  const [statusDialog, setStatusDialog] = useState({ open: false, orgId: null, newStatus: false });

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const isAdmin = () => {
    try {
      const authInfo = JSON.parse(localStorage.getItem("authInfo"));
      return authInfo && authInfo.roles && authInfo.roles.includes("ROOT_ADMIN");
    } catch {
      return false;
    }
  };

  const handleStatusChange = async (orgId, newStatus) => {
    try {
      await AppService.put(`v1/serviceMapping/${orgId}/active?isActive=${newStatus}`);
      setRows(prev => prev.map(r => (r.id === orgId ? { ...r, active: newStatus } : r)));
      dispatch(showSuccessMessage('Status updated successfully'));
    } catch (err) {
      // Error handled globally
    }
  };

  useEffect(() => {
    dispatch(updateScreenTitle("Customer Organisation Charge Mappings"));
    return () => dispatch(updateScreenTitle(""));
  }, [dispatch]);

  // Debounce the search input (400ms) before issuing the API call.
  useEffect(() => {
    const t = setTimeout(() => setSearchTerm(searchInput), 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  // Reset to page 0 whenever a filter / search / page size changes — the result
  // set may shrink and the current page index would otherwise be out of range.
  useEffect(() => {
    setPage(0);
  }, [searchTerm, selectedOrg, rowsPerPage]);

  // Fetch the org dropdown once.
  useEffect(() => {
    AppService.get('v1/organisation/keyValues', { params: { type: 'CUSTOMER' } })
      .then((resp) => setOrganisations(Array.isArray(resp) ? resp : []))
      .catch(() => setOrganisations([]));
  }, []);

  // Server-paginated rows.
  useEffect(() => {
    setIsLoading(true);
    const params = {
      orgType: 'CUSTOMER',
      page,
      pageSize: rowsPerPage,
    };
    if (selectedOrg && selectedOrg !== 'all') params.organisationId = selectedOrg;
    if (searchTerm && searchTerm.trim()) params.search = searchTerm.trim();

    AppService.get('v1/serviceMapping/all', { params })
      .then((response) => {
        // Spring's Page<T> serializes as { content, totalElements, ... }.
        // Be defensive: fall back to array shape if some caller still returns a list.
        if (response && Array.isArray(response.content)) {
          setRows(response.content);
          setTotalCount(response.totalElements ?? response.content.length);
        } else if (Array.isArray(response)) {
          setRows(response);
          setTotalCount(response.length);
        } else {
          setRows([]);
          setTotalCount(0);
        }
      })
      .catch(() => {
        setRows([]);
        setTotalCount(0);
      })
      .finally(() => setIsLoading(false));
  }, [page, rowsPerPage, searchTerm, selectedOrg]);

  const _navigateToCreationScreen = () => {
    navigate("/ADM-Customer/adm-customer-service-mapping");
  };

  const _navigateToEditScreen = (item) => {
    navigate("/ADM-Customer/adm-customer-service-mapping", { state: { item } });
  };

  return (
    <Box sx={{ p: 2 }}>
      {/* Filter Bar */}
      <Box
        sx={{
          mb: 2,
          display: 'flex',
          gap: 2,
          alignItems: 'center',
          flexWrap: 'wrap',
        }}
      >
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel id="org-filter-label">
            <Box display="flex" alignItems="center" gap={0.5}>
              <FilterListIcon sx={{ fontSize: 16 }} />
              Organisation
            </Box>
          </InputLabel>
          <Select
            labelId="org-filter-label"
            value={selectedOrg}
            label="Organisation"
            onChange={(e) => setSelectedOrg(e.target.value)}
          >
            <MenuItem value="all">All Organisations</MenuItem>
            {organisations.map(org => (
              <MenuItem key={org.id} value={org.id}>{org.name}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          placeholder="Search by organisation or service..."
          variant="outlined"
          size="small"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          sx={{ width: 320 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />

        <Typography sx={{ fontSize: 13, color: '#757575', ml: 'auto' }}>
          {totalCount} mapping{totalCount === 1 ? '' : 's'}
        </Typography>

        {isAdmin() && (
          <Button
            variant="contained"
            size="small"
            startIcon={<AddIcon />}
            onClick={_navigateToCreationScreen}
            sx={{ backgroundColor: THEME_COLOR, '&:hover': { backgroundColor: '#00796b' } }}
          >
            Create New
          </Button>
        )}
      </Box>

      <Paper sx={{ width: '100%', overflow: 'hidden' }}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: '#064d51' }}>
                <TableCell sx={{ width: '17%', color: 'white', fontWeight: 600 }}>Organisation</TableCell>
                <TableCell sx={{ width: '22%', color: 'white', fontWeight: 600 }}>Service Name</TableCell>
                <TableCell align="right" sx={{ width: '12%', color: 'white', fontWeight: 600 }}>Govt. Charge</TableCell>
                <TableCell align="right" sx={{ width: '12%', color: 'white', fontWeight: 600 }}>Service Charge</TableCell>
                <TableCell align="right" sx={{ width: '8%', color: 'white', fontWeight: 600 }}>Tax</TableCell>
                <TableCell align="right" sx={{ width: '10%', color: 'white', fontWeight: 600 }}>Change</TableCell>
                <TableCell sx={{ width: '12%', color: 'white', fontWeight: 600 }}>Status</TableCell>
                <TableCell align="center" sx={{ width: '7%', color: 'white', fontWeight: 600 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                    <CircularProgress size={28} />
                  </TableCell>
                </TableRow>
              ) : rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                    <Typography variant="body2" color="textSecondary">
                      No mappings found matching your criteria.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((item) => {
                  // Compare the customer-specific (service charge + tax) against the
                  // service's default (service charge + tax). Govt charge is a pass-through
                  // fee and not part of what the customer is being marked up/down on, so
                  // it's excluded from the comparison.
                  const num = (v) => (v == null || v === '' || Number.isNaN(parseFloat(v)) ? 0 : parseFloat(v));
                  const mappedTotal = num(item.serviceCharge) + num(item.taxOnServiceCharge);
                  const baseTotal = num(item.defaultServiceCharge) + num(item.defaultTaxOnServiceCharge);
                  const hasBase =
                    item.defaultServiceCharge != null ||
                    item.defaultTaxOnServiceCharge != null;
                  const change = mappedTotal - baseTotal;
                  const changeColor = change > 0 ? '#2e7d32' : change < 0 ? '#c62828' : '#9e9e9e';
                  const changeSign = change > 0 ? '+' : '';

                  return (
                    <TableRow key={item.id} hover>
                      <TableCell>
                        <Typography sx={{ fontSize: 13 }}>{item.organisationName}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography sx={{ fontSize: 13 }}>{item.serviceName}</Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                          <Typography sx={{ fontSize: 13 }}>{item.govtCharge}</Typography>
                          {item.defaultGovtCharge != null && (
                            <Typography sx={{ fontSize: 11, color: '#9e9e9e', mt: 0.25 }}>
                              Base: {item.defaultGovtCharge}
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell align="right">
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                          <Typography sx={{ fontSize: 13 }}>{item.serviceCharge}</Typography>
                          {item.defaultServiceCharge != null && (
                            <Typography sx={{ fontSize: 11, color: '#9e9e9e', mt: 0.25 }}>
                              Base: {item.defaultServiceCharge}
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell align="right">
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                          <Typography sx={{ fontSize: 13 }}>{item.taxOnServiceCharge}</Typography>
                          {item.defaultTaxOnServiceCharge != null && (
                            <Typography sx={{ fontSize: 11, color: '#9e9e9e', mt: 0.25 }}>
                              Base: {item.defaultTaxOnServiceCharge}
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell align="right">
                        {hasBase ? (
                          <Tooltip
                            arrow
                            title={`Mapped (service + tax) ${mappedTotal.toFixed(2)} vs base ${baseTotal.toFixed(2)}`}
                          >
                            <Typography sx={{ fontSize: 13, fontWeight: 600, color: changeColor }}>
                              {changeSign}{change.toFixed(2)}
                            </Typography>
                          </Tooltip>
                        ) : (
                          <Typography sx={{ fontSize: 13, color: '#9e9e9e' }}>—</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Typography
                            sx={{
                              fontSize: 12,
                              fontWeight: 600,
                              color: item.active ? THEME_COLOR : '#9e9e9e',
                            }}
                          >
                            {item.active ? 'Active' : 'Inactive'}
                          </Typography>
                          <Switch
                            checked={item.active}
                            size="small"
                            sx={{
                              '& .MuiSwitch-switchBase.Mui-checked': { color: THEME_COLOR },
                              '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: THEME_COLOR },
                            }}
                            onChange={() => {
                              setStatusDialog({ open: true, orgId: item.id, newStatus: !item.active });
                            }}
                          />
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="Edit Full Details" arrow>
                          <IconButton
                            size="small"
                            onClick={() => _navigateToEditScreen(item)}
                            sx={{
                              backgroundColor: '#f5f5f5',
                              border: '1px solid #e0e0e0',
                              borderRadius: '6px',
                              p: 0.5,
                              '&:hover': {
                                backgroundColor: THEME_COLOR,
                                borderColor: THEME_COLOR,
                                '& .MuiSvgIcon-root': { color: '#fff' },
                              },
                            }}
                          >
                            <EditIcon sx={{ fontSize: 16, color: '#616161' }} />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => setRowsPerPage(parseInt(e.target.value, 10))}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </Paper>

      {/* Status Change Dialog */}
      <Dialog open={statusDialog.open} onClose={() => setStatusDialog({ ...statusDialog, open: false })}>
        <DialogTitle>Confirm Status Change</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to set status to <b>{statusDialog.newStatus ? 'Active' : 'Inactive'}</b>?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStatusDialog({ ...statusDialog, open: false })}>Cancel</Button>
          <Button
            variant="contained"
            sx={{ backgroundColor: THEME_COLOR, '&:hover': { backgroundColor: '#00796b' } }}
            onClick={() => {
              handleStatusChange(statusDialog.orgId, statusDialog.newStatus);
              setStatusDialog({ ...statusDialog, open: false });
            }}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CustomerServiceMappings;
